import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pin } from 'lucide-react';
import { ChatThread } from '../../types/chat';
import { Skeleton } from '../ui/skeleton';
import { sortThreadsForHistory } from './chatHistory';
import { cn } from '../../lib/utils';

interface ChatThreadHistoryMenuProps {
  threads: ChatThread[];
  currentThreadId?: string;
  loading: boolean;
  pinningThreadId: string | null;
  dateLocale: string;
  onSelect: (thread: ChatThread) => void;
  onTogglePin: (event: React.MouseEvent<HTMLButtonElement>, thread: ChatThread) => void;
}

interface ThreadGroupProps extends Omit<ChatThreadHistoryMenuProps, 'threads' | 'loading'> {
  threads: ChatThread[];
  heading: string;
  headingClassName: string;
}

const ThreadGroup: React.FC<ThreadGroupProps> = ({
  threads,
  heading,
  headingClassName,
  currentThreadId,
  pinningThreadId,
  dateLocale,
  onSelect,
  onTogglePin,
}) => {
  const { t } = useTranslation();

  if (threads.length === 0) return null;

  return (
    <>
      <h3 className={cn('px-2 pb-1 pt-2 text-xs font-semibold tracking-wide', headingClassName)}>
        {heading}
      </h3>
      <ul>
        {threads.map((thread) => {
          const isCurrent = thread.id === currentThreadId;
          const title = thread.title || t('chat.newChat');

          return (
            <li
              key={thread.id}
              className={cn(
                'flex items-center gap-1 rounded-md border-l-2 border-transparent pl-1.5 pr-1',
                'transition-colors',
                // Hover was `backgroundColor: "currentColor"`, which paints the
                // row in its own text colour — near-black in light mode.
                thread.isPinned ? 'bg-warning/5 hover:bg-warning/10' : 'hover:bg-accent',
                // Hover and current share a background, so the bar is what
                // actually says which thread you are reading.
                isCurrent && 'border-l-primary bg-accent',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(thread)}
                className="min-w-0 flex-1 rounded-sm py-1.5 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className={cn('block truncate text-sm', isCurrent && 'font-medium')}>
                  {title}
                </span>
                <span className="block text-xs revy-figure text-muted-foreground">
                  {new Date(thread.updatedAt).toLocaleDateString(dateLocale, {
                    month: 'numeric',
                    day: 'numeric',
                  })}
                </span>
              </button>

              <button
                type="button"
                aria-label={t(thread.isPinned ? 'chat.unpinThreadAria' : 'chat.pinThreadAria', {
                  title,
                })}
                title={thread.isPinned ? t('chat.unpinThread') : t('chat.pinThread')}
                disabled={pinningThreadId === thread.id}
                onClick={(event) => onTogglePin(event, thread)}
                className={cn(
                  'grid size-8 shrink-0 place-items-center rounded transition-colors',
                  'hover:bg-accent hover:text-warning',
                  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
                  'disabled:pointer-events-none disabled:opacity-50',
                  // Was a hardcoded amber that never adapted to the dark surface.
                  thread.isPinned ? 'text-warning' : 'text-muted-foreground',
                )}
              >
                {/* One icon, filled when pinned — a slashed pin would read as
                    "unpin" on the rows that are not pinned yet. */}
                <Pin
                  className={cn('size-3.5', thread.isPinned && 'fill-current')}
                  strokeWidth={2}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
};

/** Thread picker shown under the chat header; pinned threads lead. */
const ChatThreadHistoryMenu: React.FC<ChatThreadHistoryMenuProps> = ({
  threads,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();

  const sorted = sortThreadsForHistory(threads);
  const groups = [
    {
      heading: t('chat.pinned'),
      headingClassName: 'text-warning',
      threads: sorted.filter((thread) => thread.isPinned),
    },
    {
      heading: t('chat.recentChats'),
      headingClassName: 'text-muted-foreground',
      threads: sorted.filter((thread) => !thread.isPinned),
    },
  ];

  return (
    <div className="w-[280px]">
      <p className="border-b px-3 py-2 text-xs text-muted-foreground">{t('chat.history')}</p>

      {/* Plain overflow, not ScrollArea: its viewport wraps children in a
          display:table element, which grows past w-[280px] and defeats the
          title truncation. */}
      <div className="max-h-[360px] overflow-y-auto overscroll-contain">
        {loading ? (
          <div data-testid="thread-history-loading" className="space-y-2 p-3">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} className="h-9 w-full" />
            ))}
          </div>
        ) : threads.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('chat.noHistory')}
          </p>
        ) : (
          <div className="p-1">
            {groups.map((group) => (
              <ThreadGroup key={group.heading} {...group} {...rest} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatThreadHistoryMenu;
